import { useState, useEffect, useCallback } from 'react';
import { callsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { Phone, PhoneCall, PhoneOff, Clock, Settings, Plus, Trash2, UserCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { AdminPageShell } from '../components/AdminShared';

export default function AdminCallSettingsPage() {
  const [settings, setSettings] = useState(null);
  const [phones, setPhones] = useState([]);
  const [callLog, setCallLog] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [rateLimits, setRateLimits] = useState(null);
  const [twilioNumber, setTwilioNumber] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testCallNumber, setTestCallNumber] = useState('');
  const [testingCall, setTestingCall] = useState(false);
  const [showAddPhone, setShowAddPhone] = useState(false);
  const [newPhone, setNewPhone] = useState('');

  const load = useCallback(async () => {
    try {
      const [sRes, pRes, lRes, aRes, rlRes, tnRes] = await Promise.all([
        callsAPI.getSettings(), callsAPI.listPhones(), callsAPI.getCallLog(), callsAPI.getActiveCalls(),
        callsAPI.getRateLimits().catch(() => ({ data: null })),
        callsAPI.getTwilioNumber().catch(() => ({ data: { phone_number: '' } })),
      ]);
      setSettings(sRes.data);
      setPhones(pRes.data);
      setCallLog(lRes.data);
      setActiveCalls(aRes.data);
      if (rlRes.data) setRateLimits(rlRes.data);
      setTwilioNumber(tnRes.data?.phone_number || '');
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      const [res] = await Promise.all([
        callsAPI.updateSettings(settings),
        callsAPI.setTwilioNumber({ phone_number: twilioNumber }).catch(() => {}),
      ]);
      setSettings(res.data);
      toast.success('Call settings saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const addPhone = async () => {
    if (!newPhone.trim()) return;
    try {
      await callsAPI.registerPhone({ phone_number: newPhone.trim() });
      toast.success('Phone registered');
      setNewPhone('');
      setShowAddPhone(false);
      const pRes = await callsAPI.listPhones();
      setPhones(pRes.data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const removePhone = async (phone) => {
    try {
      await callsAPI.unregisterPhone(phone);
      toast.success('Phone removed');
      setPhones(p => p.filter(ph => ph.phone_number !== phone));
    } catch { toast.error('Failed'); }
  };

  const statusColor = (s) => {
    if (s === 'in_progress' || s === 'ringing') return 'bg-green-500/10 text-green-600';
    if (s === 'connecting' || s === 'waiting_interpreter') return 'bg-amber-500/10 text-amber-600';
    if (s === 'ended') return 'bg-secondary text-muted-foreground';
    if (s === 'rejected') return 'bg-red-500/10 text-red-600';
    return '';
  };

  const handleTestCall = async () => {
    if (!testCallNumber.trim()) { toast.error('Enter a phone number to call'); return; }
    setTestingCall(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/calls/test-call`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ phone_number: testCallNumber.trim() }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(`Test call initiated! Call SID: ${data.call_sid?.slice(-8) || 'OK'}`);
      } else {
        toast.error(data.detail || 'Test call failed');
      }
    } catch (e) { toast.error('Failed to initiate test call'); }
    setTestingCall(false);
  };

  if (!settings) return <AdminPageShell loading={true} />;

  return (
    <AdminPageShell title="Phone Call System" subtitle="Configure inbound call routing and interpreter connections" testId="admin-call-settings-page" loading={loading}>

      <Tabs defaultValue="settings" className="w-full">
        <TabsList className="mb-4">
          <TabsTrigger value="settings" data-testid="call-settings-tab"><Settings className="w-4 h-4 mr-1" />Settings</TabsTrigger>
          <TabsTrigger value="phones" data-testid="call-phones-tab"><Phone className="w-4 h-4 mr-1" />Registered Phones ({phones.length})</TabsTrigger>
          <TabsTrigger value="active" data-testid="call-active-tab"><PhoneCall className="w-4 h-4 mr-1" />Active ({activeCalls.length})</TabsTrigger>
          <TabsTrigger value="log" data-testid="call-log-tab"><Clock className="w-4 h-4 mr-1" />Call Log ({callLog.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="settings">
          {rateLimits && (
            <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 mb-4 max-w-lg" data-testid="rate-limit-banner">
              <p className="text-sm font-semibold text-amber-600 mb-1">Dev Rate Limits Active</p>
              <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <span>Max call duration:</span><span className="font-mono">{Math.floor(rateLimits.max_call_duration_seconds / 60)} min</span>
                <span>Daily call limit:</span><span className="font-mono">{rateLimits.max_calls_per_day}</span>
                <span>Calls today:</span><span className="font-mono">{rateLimits.calls_today}</span>
                <span>Remaining:</span><span className="font-mono">{rateLimits.calls_remaining}</span>
              </div>
              <p className="text-[10px] text-amber-600/70 mt-2">Remember to disable before production deployment</p>
            </div>
          )}
          <div className="rounded-lg border bg-card p-6 space-y-6 max-w-lg">
            <div className="flex items-center justify-between">
              <div><Label className="text-base font-semibold">Call-In Enabled</Label><p className="text-xs text-muted-foreground">Allow inbound phone calls to this organization</p></div>
              <Switch checked={settings.call_enabled ?? true} onCheckedChange={v => setSettings(s => ({ ...s, call_enabled: v }))} data-testid="call-enabled-switch" />
            </div>

            <div className="space-y-2">
              <Label>Default Interpreter Mode</Label>
              <Select value={settings.interpreter_mode || 'ai_first'} onValueChange={v => setSettings(s => ({ ...s, interpreter_mode: v }))}>
                <SelectTrigger data-testid="interpreter-mode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="ai_first">AI First (escalate to human)</SelectItem>
                  <SelectItem value="ai_only">AI Only</SelectItem>
                  <SelectItem value="human_only">Human Only</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">How callers are initially connected to an interpreter</p>
            </div>

            <div className="space-y-2">
              <Label>Interpreter Connection Method</Label>
              <Select value={settings.connection_method || 'web_app'} onValueChange={v => setSettings(s => ({ ...s, connection_method: v }))}>
                <SelectTrigger data-testid="connection-method-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="web_app">Web App Only</SelectItem>
                  <SelectItem value="phone">Phone Only</SelectItem>
                  <SelectItem value="both">Both (Web App priority)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">How human interpreters are notified of incoming calls</p>
            </div>

            {(settings.connection_method === 'both' || settings.connection_method === 'phone') && (
              <div className="space-y-2">
                <Label>Phone Dial Delay (seconds)</Label>
                <Input type="number" min={0} max={120} value={settings.phone_dial_delay_seconds ?? 15}
                  onChange={e => setSettings(s => ({ ...s, phone_dial_delay_seconds: parseInt(e.target.value) || 0 }))}
                  data-testid="phone-delay-input" />
                <p className="text-xs text-muted-foreground">Seconds to wait for web app response before dialing interpreter's phone</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Twilio Phone Number</Label>
              <Input type="tel" value={twilioNumber}
                onChange={e => setTwilioNumber(e.target.value)}
                placeholder="+12133377765"
                className="font-mono tracking-wider"
                data-testid="twilio-number-input" />
              <p className="text-xs text-muted-foreground">The phone number callers dial to reach this system</p>
            </div>

            <div className="space-y-2">
              <Label>Organization Access PIN</Label>
              <Input type="text" maxLength={6} value={settings.access_pin || ''}
                onChange={e => setSettings(s => ({ ...s, access_pin: e.target.value.replace(/\D/g, '') }))}
                placeholder="6-digit PIN"
                className="font-mono tracking-wider"
                data-testid="access-pin-input" />
              <p className="text-xs text-muted-foreground">Callers without a registered phone must enter this PIN</p>
            </div>

            <Button onClick={saveSettings} disabled={saving} className="bg-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/90 text-white" data-testid="save-call-settings-btn">
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </div>

          {/* Test Call Section */}
          <div className="rounded-lg border bg-card p-6 space-y-4 max-w-lg mt-4" data-testid="test-call-section">
            <div>
              <Label className="text-base font-semibold">Test Call</Label>
              <p className="text-xs text-muted-foreground">Initiate a test call to verify your Twilio configuration. The system will call the number and play a test greeting.</p>
            </div>
            <div className="flex gap-2">
              <Input
                type="tel"
                value={testCallNumber}
                onChange={e => setTestCallNumber(e.target.value)}
                placeholder="+1234567890"
                className="font-mono tracking-wider flex-1"
                data-testid="test-call-number-input"
              />
              <Button
                onClick={handleTestCall}
                disabled={testingCall || !testCallNumber.trim()}
                variant="outline"
                className="border-[hsl(var(--brand))]/50 text-[hsl(var(--brand))] hover:bg-[hsl(var(--brand))]/10"
                data-testid="test-call-button"
              >
                {testingCall ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Phone className="w-4 h-4 mr-2" />}
                {testingCall ? 'Calling...' : 'Test Call'}
              </Button>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="phones">
          <div className="rounded-lg border bg-card">
            <div className="p-4 border-b flex items-center justify-between">
              <p className="text-sm font-medium">Registered phone numbers for caller validation</p>
              <Button size="sm" onClick={() => setShowAddPhone(true)} data-testid="add-phone-btn"><Plus className="w-4 h-4 mr-1" />Add Phone</Button>
            </div>
            <Table>
              <TableHeader><TableRow><TableHead>Phone Number</TableHead><TableHead>User ID</TableHead><TableHead>Registered</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {phones.map(p => (
                  <TableRow key={p.phone_number}>
                    <TableCell className="font-mono">{p.phone_number}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.user_id}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{p.registered_at ? new Date(p.registered_at).toLocaleDateString() : '-'}</TableCell>
                    <TableCell><Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => removePhone(p.phone_number)}><Trash2 className="w-3.5 h-3.5" /></Button></TableCell>
                  </TableRow>
                ))}
                {phones.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">No registered phones</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="active">
          <div className="rounded-lg border bg-card">
            <Table>
              <TableHeader><TableRow><TableHead>Call SID</TableHead><TableHead>Caller</TableHead><TableHead>Language</TableHead><TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead>Started</TableHead></TableRow></TableHeader>
              <TableBody>
                {activeCalls.map(c => (
                  <TableRow key={c.id}>
                    <TableCell className="font-mono text-xs">{(c.call_sid || '').slice(0, 12)}...</TableCell>
                    <TableCell className="font-mono text-xs">{c.caller_phone}</TableCell>
                    <TableCell><Badge variant="outline">{c.language}</Badge></TableCell>
                    <TableCell className="text-xs">{c.connection_type || c.interpreter_mode}</TableCell>
                    <TableCell><Badge className={`${statusColor(c.status)} text-[10px]`}>{c.status}</Badge></TableCell>
                    <TableCell className="text-xs">{c.started_at ? new Date(c.started_at).toLocaleTimeString() : '-'}</TableCell>
                  </TableRow>
                ))}
                {activeCalls.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-6 text-muted-foreground">No active calls</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="log">
          <div className="rounded-lg border bg-card">
            <ScrollArea className="h-[calc(100vh-16rem)]">
              <Table>
                <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Caller</TableHead><TableHead>Language</TableHead><TableHead>Type</TableHead><TableHead>Duration</TableHead><TableHead>Status</TableHead><TableHead>Reason</TableHead></TableRow></TableHeader>
                <TableBody>
                  {callLog.map(c => (
                    <TableRow key={c.id}>
                      <TableCell className="text-xs">{c.started_at ? new Date(c.started_at).toLocaleString() : '-'}</TableCell>
                      <TableCell className="font-mono text-xs">{c.caller_phone || '-'}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{c.language || '-'}</Badge></TableCell>
                      <TableCell className="text-xs">{c.connection_type || '-'}</TableCell>
                      <TableCell className="font-mono text-xs">{c.duration_seconds ? `${c.duration_seconds}s` : '-'}</TableCell>
                      <TableCell><Badge className={`${statusColor(c.status)} text-[10px]`}>{c.status}</Badge></TableCell>
                      <TableCell className="text-xs text-muted-foreground">{c.end_reason || '-'}</TableCell>
                    </TableRow>
                  ))}
                  {callLog.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">No call history</TableCell></TableRow>}
                </TableBody>
              </Table>
            </ScrollArea>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={showAddPhone} onOpenChange={setShowAddPhone}>
        <DialogContent data-testid="add-phone-dialog">
          <DialogHeader><DialogTitle>Register Phone Number</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <Label>Phone Number (E.164 format)</Label>
            <Input placeholder="+15551234567" value={newPhone} onChange={e => setNewPhone(e.target.value)} data-testid="phone-number-input" />
            <p className="text-xs text-muted-foreground">Include country code (e.g., +1 for US). This phone will bypass PIN entry.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowAddPhone(false)}>Cancel</Button><Button onClick={addPhone} className="bg-[hsl(var(--brand))] text-white" data-testid="register-phone-btn">Register</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminPageShell>
  );
}
